import { useCallback, useEffect, useState } from "react";
import { Grid, Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import axios from "../../../global/functions/apiClient";
import { getAuthHeaders } from "../../../global/functions/authorization";
import AlertDialog from "../../../components/alertDialog";
import {
  getSettings,
  getSettingsError,
  getSettingsStatus,
  selectAllSettings,
} from "../../../features/settingSlice";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import LinkIcon from "@mui/icons-material/Link";
import LanIcon from "@mui/icons-material/Lan";
import AppBarSearch from "../../../global/appBarSearch";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import CopyLink from "../../../components/copyLink";
import { useTranslation } from "react-i18next";
import {
  InfoChip,
  SectionCard,
  SettingRow,
  SettingValue,
  useSettingsCardSx,
} from "../../../components/settingsLayout";

const apiUrl = process.env.REACT_APP_API_URL;

const queueActionSx = {
  flex: { xs: 1, sm: "0 0 auto" },
  minHeight: 40,
  px: 1.5,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

function QueueJob({ job, currency, pending, onToggle, onRemove, t }) {
  const busy = pending === job.id;
  const title = `${
    job.commandNumber != null ? `#${job.commandNumber}` : t("settings.queueTicket")
  }${job.customerName ? ` · ${job.customerName}` : ""}`;
  const when = job.createdAt ? new Date(job.createdAt).toLocaleString() : "";
  const amount =
    job.total != null ? `${Number(job.total).toFixed(2)} ${currency || ""}`.trim() : "";
  const toggleLabel = job.disabled ? t("common.activate") : t("common.deactivate");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1.5,
        p: 1.5,
        borderRadius: "12px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={job.disabled ? t("settings.queueDisabled") : t("settings.queueWaiting")}
            color={job.disabled ? "default" : "success"}
            variant={job.disabled ? "outlined" : "filled"}
            sx={{ flexShrink: 0, fontWeight: 700 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {[when, amount].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <Button
          size="small"
          disabled={busy}
          startIcon={
            job.disabled ? (
              <PlayCircleOutlineIcon />
            ) : (
              <PauseCircleOutlineIcon />
            )
          }
          onClick={() => onToggle(job)}
          sx={{
            ...queueActionSx,
            color: "text.primary",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            "&:hover": { bgcolor: "action.selected" },
          }}
        >
          {toggleLabel}
        </Button>
        <Button
          size="small"
          disabled={busy}
          startIcon={<DeleteOutlineIcon />}
          onClick={() => onRemove(job)}
          sx={{
            ...queueActionSx,
            color: "error.contrastText",
            bgcolor: "error.main",
            "&:hover": { bgcolor: "error.dark" },
          }}
        >
          {t("common.delete")}
        </Button>
      </Box>
    </Box>
  );
}

const PrinterManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const settings = useSelector(selectAllSettings);
  const error = useSelector(getSettingsError);
  const settingStatus = useSelector(getSettingsStatus);
  const [copied, setCopied] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [queueError, setQueueError] = useState("");
  const [queueLoading, setQueueLoading] = useState(true);
  const [pendingId, setPendingId] = useState("");
  const [jobToRemove, setJobToRemove] = useState(null);
  const cardSx = useSettingsCardSx();
  const isLocal = settings?.printMode === false;

  const loadQueue = useCallback(
    async (silent = false) => {
      if (!restaurantId) {
        setJobs([]);
        setQueueLoading(false);
        return;
      }
      if (!silent) setQueueLoading(true);
      try {
        const response = await axios.get(
          `${apiUrl}/settings/print-queue`,
          getAuthHeaders(false, restaurantId)
        );
        setJobs(response.data?.jobs || []);
        setQueueError("");
      } catch (err) {
        setQueueError(err.response?.data?.message || t("settings.queueError"));
      } finally {
        if (!silent) setQueueLoading(false);
      }
    },
    [restaurantId, t]
  );

  const toggleJob = async (job) => {
    setPendingId(job.id);
    try {
      await axios.patch(
        `${apiUrl}/settings/print-queue/${job.id}`,
        { disabled: !job.disabled },
        getAuthHeaders(false, restaurantId)
      );
      await loadQueue(true);
    } catch (err) {
      setQueueError(err.response?.data?.message || t("settings.queueError"));
    } finally {
      setPendingId("");
    }
  };

  const removeJob = async () => {
    if (!jobToRemove) return;
    setPendingId(jobToRemove.id);
    try {
      await axios.delete(
        `${apiUrl}/settings/print-queue/${jobToRemove.id}`,
        getAuthHeaders(false, restaurantId)
      );
      await loadQueue(true);
    } catch (err) {
      setQueueError(err.response?.data?.message || t("settings.queueError"));
    } finally {
      setPendingId("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("modify", {
      state: {
        printer: {
          printMode: settings.printMode,
          printerIp: settings.printerIp,
        },
      },
    });
  };

  useEffect(() => {
    dispatch(getSettings(restaurantId));
  }, [dispatch, restaurantId]);

  useEffect(() => {
    loadQueue(false);
    const timer = setInterval(() => loadQueue(true), 5000);
    return () => clearInterval(timer);
  }, [loadQueue]);

  return (
    <div className="main-application">
      <AppBarSearch
        handleSubmit={handleSubmit}
        title={t("pages.printerManagement")}
        buttonTitle={t("actions.editPrinterSettings")}
        buttonColor={"info"}
        buttonIcon={<EditIcon />}
      />
      <main>
        <Box sx={{ my: 2 }}>
          {settingStatus === "loading" ? (
            <Loading />
          ) : settingStatus === "fetchError" ? (
            <Error>{error}</Error>
          ) : (
            settings && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={<PrintIcon color="primary" />}
                    title={t("settings.sectionPrinter")}
                  >
                    <SettingRow label={t("fields.printerMode")} last>
                      <InfoChip
                        label={
                          isLocal
                            ? t("fields.printLocal")
                            : t("fields.printAuto")
                        }
                      />
                    </SettingRow>
                  </SectionCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={
                      isLocal ? (
                        <LanIcon color="primary" />
                      ) : (
                        <LinkIcon color="primary" />
                      )
                    }
                    title={t("settings.sectionConnection")}
                  >
                    {isLocal ? (
                      <SettingRow label={t("fields.printerIp")} last>
                        <SettingValue>{settings?.printerIp}</SettingValue>
                      </SettingRow>
                    ) : (
                      <SettingRow label={t("fields.printerUrl")} stacked last>
                        {settings?.printerUrl ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.25,
                              py: 0.75,
                              width: "100%",
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              bgcolor: "action.hover",
                            }}
                          >
                            <LinkIcon fontSize="small" color="action" />
                            <CopyLink
                              copied={copied}
                              setCopied={setCopied}
                              url={settings.printerUrl}
                            />
                          </Box>
                        ) : (
                          <SettingValue />
                        )}
                      </SettingRow>
                    )}
                  </SectionCard>
                </Grid>
                <Grid item xs={12}>
                  <SectionCard
                    sx={cardSx}
                    icon={<PrintIcon color="primary" />}
                    title={t("settings.sectionQueue")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                        {t("settings.queueHint")}
                      </Typography>
                      <Tooltip title={t("settings.queueRefresh")}>
                        <IconButton
                          size="small"
                          aria-label={t("settings.queueRefresh")}
                          onClick={() => loadQueue(false)}
                          sx={{
                            flexShrink: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "10px",
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {queueLoading ? (
                      <Loading />
                    ) : queueError && jobs.length === 0 ? (
                      <Error>{queueError}</Error>
                    ) : jobs.length === 0 ? (
                      <Typography color="text.secondary">
                        {t("settings.queueEmpty")}
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {queueError ? <Error>{queueError}</Error> : null}
                        {jobs.map((job) => (
                          <QueueJob
                            key={job.id}
                            job={job}
                            currency={settings.defaultCurrency}
                            pending={pendingId}
                            onToggle={toggleJob}
                            onRemove={setJobToRemove}
                            t={t}
                          />
                        ))}
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>
            )
          )}
        </Box>
      </main>
      <AlertDialog
        open={Boolean(jobToRemove)}
        handleClose={() => setJobToRemove(null)}
        title={t("common.delete")}
        message={t("settings.queueRemoveConfirm")}
        deleteData={removeJob}
      />
    </div>
  );
};

export default PrinterManagement;
